import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Bot as BotIcon } from "lucide-react";
import { api, tradeAuth, toastApiError } from "@/lib/api";

const StatusDot = ({ s }) => (
  <span className={`h-2 w-2 rounded-full ${s === "won" ? "bg-[color:var(--naevo-success)]" : s === "lost" ? "bg-[color:var(--naevo-danger)]" : "bg-white/40 animate-pulse"}`} />
);

export const AutopilotPanel = ({ sessionId }) => {
  const auth = tradeAuth();
  const [status, setStatus] = useState({ enabled: false, stake: 0.35, daily_cap: 20, today_trades: 0, recent: [] });
  const [saving, setSaving] = useState(false);
  const [stake, setStake] = useState(0.35);
  const [cap, setCap] = useState(20);

  const pull = () => api.get("/autopilot/status", { params: { session_id: sessionId } }).then((r) => {
    setStatus(r.data);
    setStake((s) => (r.data.stake && !saving ? r.data.stake : s));
    setCap(r.data.daily_cap || 20);
  }).catch(() => {});
  useEffect(() => {
    if (!sessionId) return;
    pull();
    const t = setInterval(pull, 15000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  const flip = async (enabled) => {
    setSaving(true);
    try {
      const r = await api.post("/autopilot/toggle", { session_id: sessionId, enabled, stake: Math.max(0.35, Number(stake) || 0.35), daily_cap: Number(cap) });
      setStatus((s) => ({ ...s, ...r.data }));
      toast.success(enabled ? `Autopilot ON on your ${auth?.type === "demo" ? "DEMO" : "REAL"} account` : "Autopilot OFF");
      pull();
    } catch (e) { toastApiError(toast, e, "Toggle failed"); } finally { setSaving(false); }
  };

  return (
    <div className="mt-6 casino-frame rounded-2xl p-5" data-testid="autopilot-panel">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-gold flex items-center gap-2"><BotIcon className="h-3.5 w-3.5" /> Autopilot · always-on bot</div>
          <div className="font-casino text-2xl text-gold-grad mt-1">{status.enabled ? "Scanning 10 markets every 60s" : "Switch on to let the bot trade for you"}</div>
          <div className="text-xs text-white/55 mt-1 font-mono-naevo" data-testid="autopilot-summary">
            {auth ? `${auth.type === "demo" ? "DEMO" : "REAL"} ${auth.account_id} · ` : ""}{status.today_trades} / {cap} today · base ${Number(stake).toFixed(2)}
            {status.recovering > 0 && <span className="text-[#F5A623]"> · winning back ${status.recovering.toFixed(2)} → next ${Number(status.next_stake).toFixed(2)}</span>}
          </div>
        </div>
        <button onClick={() => flip(!status.enabled)} disabled={saving} data-testid="autopilot-toggle-btn" aria-label="Toggle autopilot"
          className={`relative h-10 w-20 rounded-full transition-colors ${status.enabled ? "bg-emerald-500" : "bg-white/10"}`}>
          <span className={`absolute top-1 h-8 w-8 rounded-full bg-white shadow-lg transition-all ${status.enabled ? "right-1" : "left-1"}`} />
        </button>
      </div>
      <div className="grid grid-cols-2 gap-3 mt-5">
        <label className="block">
          <div className="text-[11px] uppercase tracking-widest text-white/40 mb-1">Base stake (min $0.35)</div>
          <input type="number" min="0.35" step="0.05" value={stake} onChange={(e) => setStake(e.target.value)}
            onBlur={() => { setStake(Math.max(0.35, Number(stake) || 0.35)); if (status.enabled) flip(true); }}
            className="w-full rounded-xl bg-white/5 border border-gold/20 px-3 py-2 text-white text-sm font-mono-naevo" data-testid="autopilot-stake" />
        </label>
        <label className="block">
          <div className="text-[11px] uppercase tracking-widest text-white/40 mb-1">Daily cap (trades)</div>
          <input type="number" min="1" step="1" value={cap} onChange={(e) => setCap(e.target.value)} onBlur={() => status.enabled && flip(true)}
            className="w-full rounded-xl bg-white/5 border border-gold/20 px-3 py-2 text-white text-sm font-mono-naevo" data-testid="autopilot-cap" />
        </label>
      </div>
      <p className="text-[11px] text-white/45 mt-3">After a loss the next stake wins it all back + 5%, then drops to base. Stops at your daily loss limit or profit target.</p>
      {status.recent?.length > 0 && (
        <div className="mt-4 space-y-1.5" data-testid="autopilot-recent">
          {status.recent.map((t, i) => (
            <div key={i} className="flex items-center justify-between text-xs font-mono-naevo text-white/70">
              <span className="flex items-center gap-2"><StatusDot s={t.status} /> {t.contract} {t.symbol}</span>
              <span>${Number(t.stake).toFixed(2)}</span>
              <span className={t.profit > 0 ? "text-[color:var(--naevo-success)]" : t.profit < 0 ? "text-[color:var(--naevo-danger)]" : "text-white/40"}>
                {t.status === "placed" ? "settling…" : `${t.profit >= 0 ? "+" : ""}${Number(t.profit || 0).toFixed(2)}`}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
